import { DatadogRUM } from '@user-portal/datadog-rum';
import { Box, Text } from '@user-portal/wikr-components';
import { useEffect } from 'react';
import { Trans } from 'react-i18next';

import BrowserStorage from 'api/utils/browserStorage';

import { sendRetenoAnalyticsRequest } from 'store/analytics/actions';
import { getDeepLinkRequest } from 'store/deepLink/actions';
import { selectDeepLink } from 'store/deepLink/selectors';
import { selectCancellationCandidates } from 'store/subscriptions/selectors';
import { selectCurrentUser } from 'store/user/selectors';

import { Platform, PLATFORM_BY_KEY } from 'constants/api';
import { YG_CABINET_DEEPLINK_CLICK } from 'constants/dataDog';

import {
    sendAnalyticCancelOfferClick,
    sendAnalyticDeeplinkLoadClick,
    sendAnalyticDownloadAppClick,
    trackScreenLoadCancelOffer,
} from 'services/Analytics';
import { trackScreenLoad } from 'services/Analytics/trackers/mainTrackers';

import { useDispatch, useSelector } from 'hooks/store';

import isMobile from 'helpers/isMobile';
import { LS_KEYS } from 'helpers/localStorage';
import { getValuesByKey } from 'helpers/utils';

import { CancelOfferAction } from 'types/subscription';

import CopyCodeStep from './components/CopyCodeStep';
import PlanInfo from './components/PlanInfo';
import StoreButton from './components/StoreButton';
import { IInstallAppSteps } from './types';

import * as S from './styled';

const InstallAppSteps = ({ customLink, cancelOfferName = null }: IInstallAppSteps) => {
    const dispatch = useDispatch();

    const deepLinkFromServer = useSelector(selectDeepLink);
    const { id, email, first_name } = useSelector(selectCurrentUser);

    const deepLinkUrl = customLink || deepLinkFromServer;

    const isAndroid = isMobile.android();
    const isIos = isMobile.iOS();
    const isMobileDevice = isAndroid || isIos;

    const cancellationCandidates = useSelector(selectCancellationCandidates);
    const isCancelOffer = Boolean(cancelOfferName);

    const subscription_id = getValuesByKey(cancellationCandidates, 'external_id');

    const isUserFirstTimeOnMainPageAfterOB = Boolean(localStorage.getItem(LS_KEYS.isUserFirstTimeOnMainPageAfterOB));

    useEffect(() => {
        return () => {
            if (isUserFirstTimeOnMainPageAfterOB) {
                localStorage.removeItem(LS_KEYS.isUserFirstTimeOnMainPageAfterOB);
            }
        };
    }, [isUserFirstTimeOnMainPageAfterOB]);

    useEffect(() => {
        if (!deepLinkUrl) {
            dispatch(getDeepLinkRequest());
        }
    }, [deepLinkUrl, dispatch]);

    useEffect(() => {
        isCancelOffer
            ? trackScreenLoadCancelOffer({ eventLabel: { subscription_id, offer: cancelOfferName } })
            : trackScreenLoad();

        if (isUserFirstTimeOnMainPageAfterOB && !isCancelOffer) {
            dispatch(
                sendRetenoAnalyticsRequest({
                    event_type_key: 'main__screen__load',
                    event_params: {
                        externalCustomerId: id,
                        email,
                        time: new Date().toISOString(),
                        platform: PLATFORM_BY_KEY[Platform.Web],
                    },
                })
            );
        }
    }, [isCancelOffer, dispatch, subscription_id, cancelOfferName, id, email, isUserFirstTimeOnMainPageAfterOB]);

    useEffect(() => {
        if (deepLinkUrl) {
            sendAnalyticDeeplinkLoadClick();
        }
    }, [deepLinkUrl]);

    const handleDownloadApp = (eventLabel: string, isDeeplinkBtn = true) => {
        BrowserStorage.setItem('isAppDownloaded', true);

        if (!isDeeplinkBtn) {
            window.open(deepLinkUrl, '_blank', 'noopener,noreferrer');
        }

        if (isMobile.any()) {
            DatadogRUM.addAction(YG_CABINET_DEEPLINK_CLICK, { eventLabel });
        }

        isCancelOffer
            ? sendAnalyticCancelOfferClick({
                  eventLabel: { subscription_id, offer: cancelOfferName },
                  actionEvent: CancelOfferAction.Accept,
              })
            : sendAnalyticDownloadAppClick(eventLabel);

        if (isUserFirstTimeOnMainPageAfterOB && !isCancelOffer) {
            dispatch(
                sendRetenoAnalyticsRequest({
                    event_type_key: 'main__click',
                    event_params: {
                        event_label: eventLabel,
                        externalCustomerId: id,
                        email,
                        time: new Date().toISOString(),
                        platform: PLATFORM_BY_KEY[Platform.Web],
                    },
                })
            );
        }
    };

    const descriptionText = `downloadApp.simplifiedVersion.${
        isMobileDevice ? 'mobile' : 'desktop'
    }.description--dynamic-key`;

    return (
        <S.Container>
            <PlanInfo isMobileDevice={isMobileDevice} deepLinkUrl={deepLinkUrl} />

            <Box>
                <S.Title type="h3" mb={12} medium isMobileDevice={isMobileDevice}>
                    <Trans
                        i18nKey="downloadApp.simplifiedVersion.title"
                        components={{ 1: <S.TitleHighlight /> }}
                        values={{
                            name: first_name,
                            hasName: first_name ? 'yes' : 'no',
                        }}
                    />
                </S.Title>
                <Text mb={24} type="large-text" color="text-secondary">
                    <Trans i18nKey={descriptionText} components={{ 1: <S.HighlightEmail /> }} values={{ email }} />
                </Text>

                <CopyCodeStep isMobileDevice={Boolean(isMobileDevice)} />

                {isMobileDevice && <StoreButton isIos={isIos} handleLinkDownloadApp={handleDownloadApp} />}
            </Box>
        </S.Container>
    );
};

export default InstallAppSteps;
